import React from 'react';
import { SEO } from '../components/SEO';
import { organizationSchema, localBusinessSchema, serviceSchema, websiteSchema, breadcrumbSchema, faqSchema, reviewSchema } from '../utils/structuredData';
import { Hero } from '../components/Hero';
import { ProblemSection } from '../components/ProblemSection';
import { SolutionSection } from '../components/SolutionSection';
import { BenefitsGrid } from '../components/BenefitsGrid';
import { ProcessSteps } from '../components/ProcessSteps';
import { ServicesSection } from '../components/ServicesSection';
import { CaseStudies } from '../components/CaseStudies';
import { Testimonials } from '../components/Testimonials';
import { PricingSection } from '../components/PricingSection';
import { FAQ } from '../components/FAQ';
import { FinalCTA } from '../components/FinalCTA';
export function LandingPage() {
  // FAQ Structured Data
  const faqData = [{
    question: 'How long does the Close CRM implementation take?',
    answer: 'Implementation typically takes 2-4 weeks, depending on the chosen package. This is 3x faster than the industry average.'
  }, {
    question: 'How much does Close CRM cost?',
    answer: 'Close CRM has various licensing models starting at approx. €25/user/month. Our implementation costs depend on the chosen service package. Contact us for a custom quote.'
  }, {
    question: 'Can you migrate data from our old CRM?',
    answer: 'Yes, we migrate your existing data from any CRM system. The CRM Set Up package includes migration of up to 10,000 contacts.'
  }, {
    question: 'Do you also offer ongoing support?',
    answer: 'Yes, our Managed Service package offers ongoing priority support, monthly optimization calls, and a dedicated account manager.'
  }, {
    question: 'Is Close CRM GDPR compliant?',
    answer: 'Yes, Close CRM is fully GDPR compliant. All data is securely stored in the EU with a full audit trail and deletion capabilities.'
  }];
  // Review Structured Data
  const reviewData = [{
    author: 'Florian Bretschneider',
    rating: 5,
    text: 'We have the CRM system with all processes and automations. We are now at 510,000 euros cash collect monthly with an order volume of over 800,000 euros.',
    date: '2024-01-15'
  }, {
    author: 'Albert Pappelheim',
    rating: 5,
    text: 'We work three to four times more efficiently. In one day, we easily manage 300-400 dial attempts instead of 100-150. That saves an immense amount of time.',
    date: '2024-02-20'
  }, {
    author: 'Adrian Wettig',
    rating: 5,
    text: 'The CRM makes our lives extremely easy. We were able to double our revenue from 30,000€ to 70,000€.',
    date: '2024-03-10'
  }];
  // Breadcrumb Structured Data
  const breadcrumbs = [{
    name: 'Home',
    url: 'https://close-crm-agentur.de'
  }];
  const structuredData = [organizationSchema, localBusinessSchema, serviceSchema, websiteSchema, breadcrumbSchema(breadcrumbs), faqSchema(faqData), reviewSchema(reviewData)];
  return <>
      <SEO title="Close CRM Agency for Scalable Sales | 47% More Deals" description="The leading Close CRM agency in the DACH region. We implement Close CRM so your sales team closes 47% more deals – without extra staff, without chaos, without data loss. ✓ 110+ Clients ✓ 3.2x ROI ✓ 2-4 Weeks Implementation" canonical="https://close-crm-agentur.de" structuredData={structuredData} data-id="element-508" />
      <div className="min-h-screen bg-[#0a0a0a]" data-id="element-509">
        {/* Simple Header with CTA */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/80 backdrop-blur-lg border-b border-white/5" data-id="element-510">
          <nav className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8" data-id="element-511">
            <div className="flex items-center justify-center sm:justify-between h-14 sm:h-16 lg:h-20 gap-2 sm:gap-4" data-id="element-512">
              {/* Logo */}
              <a href="/" className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0" aria-label="Close CRM Agency Homepage" data-id="element-513">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-[#0ea5e9] flex items-center justify-center flex-shrink-0" data-id="element-514">
                  <span className="text-white font-bold text-xs sm:text-sm" data-id="element-515">
                    C
                  </span>
                </div>
                <span className="text-white font-bold text-sm sm:text-base lg:text-lg whitespace-nowrap" data-id="element-516">
                  Close CRM Agency
                </span>
              </a>

              {/* CTA Button - Hidden on Mobile */}
              <a href="/free-strategy-call" className="hidden sm:flex px-5 py-2.5 bg-[#0ea5e9] text-white font-semibold rounded-lg hover:bg-[#0284c7] transition-colors text-sm items-center justify-center text-center leading-tight flex-shrink-0" data-id="element-517">
                <span className="whitespace-nowrap" data-id="element-518">Free Strategy Call</span>
              </a>
            </div>
          </nav>
        </header>

        {/* Main content */}
        <main data-id="element-519">
          <Hero data-id="element-520" />
          <ProblemSection data-id="element-521" />
          <SolutionSection data-id="element-522" />
          <div id="benefits" data-id="element-523">
            <BenefitsGrid data-id="element-524" />
          </div>
          <div id="process" data-id="element-525">
            <ProcessSteps data-id="element-526" />
          </div>
          <ServicesSection data-id="element-527" />
          <CaseStudies data-id="element-528" />
          <Testimonials data-id="element-529" />
          <PricingSection data-id="element-530" />
          <FAQ data-id="element-531" />
          <FinalCTA data-id="element-532" />
        </main>

        {/* Footer */}
        <footer className="bg-[#0a0a0a] border-t border-white/5 py-12" data-id="element-533">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-534">
            <div className="grid md:grid-cols-4 gap-8" data-id="element-535">
              {/* Brand */}
              <div className="md:col-span-2" data-id="element-536">
                <div className="flex items-center gap-2 mb-4" data-id="element-537">
                  <div className="w-8 h-8 rounded-lg bg-[#0ea5e9] flex items-center justify-center" data-id="element-538">
                    <span className="text-white font-bold text-sm" data-id="element-539">C</span>
                  </div>
                  <span className="text-white font-bold text-lg" data-id="element-540">
                    Close CRM Agency
                  </span>
                </div>
                <p className="text-gray-500 text-sm max-w-md mb-4" data-id="element-541">
                  The leading Close CRM agency in the DACH region. We have
                  helped over 110 entrepreneurs automate their sales and
                  generate more revenue.
                </p>
                <p className="text-gray-600 text-xs" data-id="element-542">
                  © {new Date().getFullYear()} Close CRM Agency. All rights
                  reserved.
                </p>
              </div>

              {/* Links */}
              <div data-id="element-543">
                <h4 className="text-white font-semibold mb-4" data-id="element-544">Navigation</h4>
                <ul className="space-y-2" data-id="element-545">
                  <li data-id="element-546">
                    <a href="#benefits" className="text-gray-500 hover:text-white transition-colors text-sm" data-id="element-547">
                      Benefits
                    </a>
                  </li>
                  <li data-id="element-548">
                    <a href="#process" className="text-gray-500 hover:text-white transition-colors text-sm" data-id="element-549">
                      Process
                    </a>
                  </li>
                  <li data-id="element-550">
                    <a href="#services" className="text-gray-500 hover:text-white transition-colors text-sm" data-id="element-551">
                      Services
                    </a>
                  </li>
                  <li data-id="element-552">
                    <a href="#faq" className="text-gray-500 hover:text-white transition-colors text-sm" data-id="element-553">
                      FAQ
                    </a>
                  </li>
                </ul>
              </div>

              {/* Contact */}
              <div data-id="element-554">
                <h4 className="text-white font-semibold mb-4" data-id="element-555">Contact</h4>
                <ul className="space-y-2 text-sm" data-id="element-556">
                  <li data-id="element-557">
                    <a href="mailto:justus@crm-kraft.de" className="text-gray-500 hover:text-white transition-colors" data-id="element-558">
                      justus@crm-kraft.de
                    </a>
                  </li>
                  <li data-id="element-559">
                    <a href="https://wa.me/4915735999071" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white transition-colors" data-id="element-560">
                      +49 157 35999071
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            {/* Legal links */}
            <div className="mt-12 pt-8 border-t border-white/5 flex flex-wrap gap-6 text-xs text-gray-600" data-id="element-561">
              <a href="/legal-notice" className="hover:text-white transition-colors" data-id="element-562">
                Legal Notice
              </a>
              <a href="/privacy-policy" className="hover:text-white transition-colors" data-id="element-563">
                Privacy Policy
              </a>
            </div>
          </div>
        </footer>
      </div>
    </>;
}